/**
 * ScamDataAggregator - Aggregates scam data from multiple sources
 * Combines Firebase data with external APIs and news sources
 */

import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import axios from 'axios';
import { aggregateExternalScamData } from './scamApiIntegrations';

// Scam severity levels
export const SCAM_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Scam types/categories
export const SCAM_TYPES = {
  PIG_BUTCHERING: 'Pig Butchering',
  RUG_PULL: 'Rug Pull',
  PONZI: 'Ponzi Scheme',
  PHISHING: 'Phishing',
  FAKE_ICO: 'Fake ICO',
  PUMP_DUMP: 'Pump & Dump',
  FAKE_EXCHANGE: 'Fake Exchange',
  ROMANCE: 'Romance Scam',
  INVESTMENT: 'Investment Fraud',
  NFT_SCAM: 'NFT Scam',
  DEFI_EXPLOIT: 'DeFi Exploit',
  EXIT_SCAM: 'Exit Scam',
  HONEYPOT: 'Honeypot',
  WALLET_DRAIN: 'Wallet Drain',
  IMPERSONATION: 'Impersonation',
  MONEY_LAUNDERING: 'Money Laundering',
  OTHER: 'Other'
};

/**
 * Geocode location string to coordinates
 */
async function geocodeLocation(location) {
  if (!location) return null;

  try {
    // Using OpenStreetMap Nominatim (free, no API key required)
    const response = await axios.get(`https://nominatim.openstreetmap.org/search`, {
      params: {
        q: location,
        format: 'json',
        limit: 1
      },
      headers: {
        'User-Agent': 'CFE-Portal/1.0'
      }
    });

    if (response.data && response.data.length > 0) {
      return {
        lat: parseFloat(response.data[0].lat),
        lng: parseFloat(response.data[0].lon),
        displayName: response.data[0].display_name
      };
    }
  } catch (error) {
    console.error('Geocoding error:', error);
  }

  return null;
}

/**
 * Extract location from scam report
 */
function extractLocationInfo(report) {
  // Check multiple possible location fields
  const locationFields = [
    report.location,
    report.targetLocation,
    report.originCountry,
    report.country,
    report.metadata?.location,
    report.metadata?.country
  ];

  for (const loc of locationFields) {
    if (loc && typeof loc === 'string' && loc.trim()) {
      return loc.trim();
    }
  }

  return null;
}

/**
 * Fetch scam data from Firebase Firestore
 */
export async function fetchFirebaseScams() {
  try {
    const scamsRef = collection(db, 'posts');
    const q = query(
      scamsRef,
      orderBy('createdAt', 'desc'),
      limit(100)
    );

    const snapshot = await getDocs(q);
    const scams = [];

    for (const doc of snapshot.docs) {
      const data = doc.data();
      const location = extractLocationInfo(data);

      if (location) {
        const coords = await geocodeLocation(location);

        if (coords) {
          scams.push({
            id: doc.id,
            title: data.title || 'Untitled Scam',
            description: data.content || data.description || '',
            type: data.category || data.scamType || SCAM_TYPES.OTHER,
            severity: data.severity || SCAM_SEVERITY.MEDIUM,
            amount: data.amount || data.lossAmount || 0,
            date: data.createdAt?.toDate?.() || new Date(),
            location: coords.displayName,
            lat: coords.lat,
            lng: coords.lng,
            source: 'CFE Reports',
            url: `/post/${doc.id}`,
            verified: data.verified || false,
            victimCount: data.victimCount || 1
          });
        }
      }
    }

    return scams;
  } catch (error) {
    console.error('Error fetching Firebase scams:', error);
    return [];
  }
}

/**
 * Fetch scam data from external APIs
 * Integrates multiple real-time data sources
 */
export async function fetchExternalScamData() {
  try {
    // Fetch from real APIs
    const externalScams = await aggregateExternalScamData();

    // Geocode locations that need coordinates
    const scamsWithCoords = [];
    for (const scam of externalScams) {
      if (scam.location && !scam.lat && !scam.lng) {
        const coords = await geocodeLocation(scam.location);
        if (coords) {
          scamsWithCoords.push({
            id: `ext-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            ...scam,
            lat: coords.lat,
            lng: coords.lng,
            location: coords.displayName || scam.location
          });
        }
      } else if (scam.lat && scam.lng) {
        scamsWithCoords.push({
          id: `ext-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          ...scam
        });
      }
    }

    // Add famous historical scams for context
    const historicalScams = [
      {
        id: 'hist-1',
        title: 'BitConnect Ponzi Scheme',
        description: 'Major cryptocurrency Ponzi scheme that defrauded investors',
        type: SCAM_TYPES.PONZI,
        severity: SCAM_SEVERITY.CRITICAL,
        amount: 2000000000,
        date: new Date('2018-01-16'),
        location: 'United States',
        lat: 37.0902,
        lng: -95.7129,
        source: 'Public Records',
        url: 'https://en.wikipedia.org/wiki/Bitconnect',
        verified: true,
        victimCount: 10000
      },
      {
        id: 'hist-2',
        title: 'OneCoin Cryptocurrency Scam',
        description: 'Fraudulent cryptocurrency scheme operating globally',
        type: SCAM_TYPES.PONZI,
        severity: SCAM_SEVERITY.CRITICAL,
        amount: 4000000000,
        date: new Date('2019-10-10'),
        location: 'Bulgaria',
        lat: 42.7339,
        lng: 25.4858,
        source: 'FBI Reports',
        url: 'https://www.fbi.gov/wanted/cyber/ruja-ignatova',
        verified: true,
        victimCount: 3500000
      },
      {
        id: 'hist-3',
        title: 'PlusToken Scam',
        description: 'Chinese Ponzi scheme targeting Asian investors',
        type: SCAM_TYPES.PONZI,
        severity: SCAM_SEVERITY.CRITICAL,
        amount: 3000000000,
        date: new Date('2019-06-27'),
        location: 'China',
        lat: 35.8617,
        lng: 104.1954,
        source: 'International Reports',
        url: '#',
        verified: true,
        victimCount: 800000
      }
    ];

    return [...scamsWithCoords, ...historicalScams];
  } catch (error) {
    console.error('Error fetching external scam data:', error);
    // Return historical scams as fallback
    return [];
  }
}

/**
 * Aggregate all scam data from multiple sources
 */
export async function aggregateAllScamData() {
  try {
    const [firebaseScams, externalScams] = await Promise.all([
      fetchFirebaseScams(),
      fetchExternalScamData()
    ]);

    const allScams = [...firebaseScams, ...externalScams];

    // Sort by date (newest first)
    allScams.sort((a, b) => b.date - a.date);

    return allScams;
  } catch (error) {
    console.error('Error aggregating scam data:', error);
    return [];
  }
}

/**
 * Calculate statistics from scam data
 */
export function calculateScamStats(scams) {
  const stats = {
    total: scams.length,
    totalAmount: 0,
    totalVictims: 0,
    byType: {},
    bySeverity: {},
    byCountry: {},
    recentScams: 0, // Last 30 days
    averageAmount: 0
  };

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  scams.forEach(scam => {
    // Total amounts and victims
    stats.totalAmount += scam.amount || 0;
    stats.totalVictims += scam.victimCount || 0;

    // By type
    stats.byType[scam.type] = (stats.byType[scam.type] || 0) + 1;

    // By severity
    stats.bySeverity[scam.severity] = (stats.bySeverity[scam.severity] || 0) + 1;

    // By country (extract from location)
    if (scam.location) {
      const country = scam.location.split(',').pop().trim();
      stats.byCountry[country] = (stats.byCountry[country] || 0) + 1;
    }

    // Recent scams
    if (scam.date >= thirtyDaysAgo) {
      stats.recentScams++;
    }
  });

  stats.averageAmount = stats.total > 0 ? stats.totalAmount / stats.total : 0;

  return stats;
}

/**
 * Filter scams by criteria
 */
export function filterScams(scams, filters) {
  return scams.filter(scam => {
    // Filter by type
    if (filters.type && filters.type !== 'all' && scam.type !== filters.type) {
      return false;
    }

    // Filter by severity
    if (filters.severity && filters.severity !== 'all' && scam.severity !== filters.severity) {
      return false;
    }

    // Filter by date range
    if (filters.startDate && scam.date < new Date(filters.startDate)) {
      return false;
    }
    if (filters.endDate && scam.date > new Date(filters.endDate)) {
      return false;
    }

    // Filter by minimum amount
    if (filters.minAmount && scam.amount < filters.minAmount) {
      return false;
    }

    // Filter by verified status
    if (filters.verifiedOnly && !scam.verified) {
      return false;
    }

    return true;
  });
}
